/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import {
	ProductDetailsCarousel,
	ProductDetailsCarouselRef,
} from '@/components/blocks/ProductDetails/Carousel';
import { getProductImageSlides } from '@/components/blocks/ProductDetails/ProductImageSlides';
import { productDetailsImageSX } from '@/components/blocks/ProductDetails/styles/image';
import { ProductImage } from '@/components/blocks/ProductImage';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { isB2BStore, useSettings } from '@/data/Settings';
import { Slide } from '@/data/types/Slide';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { replaceImagePath } from '@/utils/hdmPathConverter';
import { isKitOrBundleType } from '@/utils/productIsA';
import { Grid } from '@mui/material';
import { FC, useContext, useMemo, useState } from 'react';

export const ProductDetailsGallery: FC<{
	carouselRef?: React.RefObject<ProductDetailsCarouselRef>;
	onTotalSlidesChange?: (totalSlides: number) => void;
	onCurrentSlideChange?: (currentSlide: number) => void;
}> = ({ carouselRef, onTotalSlidesChange, onCurrentSlideChange }) => {
	const { settings } = useSettings();
	const isB2B = isB2BStore(settings);
	const { selection, product, attachments } = useContext(ContentContext) as ReturnType<
		typeof useProductDetails
	> &
		ReturnType<typeof useSkuListTable>;
	const isKitOrBundle = isKitOrBundleType[product?.type ?? 'unknown'];
	const sku = !isKitOrBundle ? selection?.sku : product;
	const { name } = getProductDisplayInfo(sku, product);
	const [index, setIndex] = useState<number>(0);
	const skuImage = replaceImagePath((isB2B ? sku?.thumbnail : sku?.fullImage) ?? '');
	const slides: Slide[] = useMemo(() => {
		if (!product) {
			return [];
		}
		return sku ? getProductImageSlides(sku, attachments, skuImage) : [];
	}, [product, sku, skuImage, attachments]);

	const showCarousel = useMemo(() => (slides?.length ?? 0) > 1, [slides]);
	const slidesImage = slides[0]?.fullImage;

	const [src, setSrc] = useState<string>(showCarousel ? slidesImage : skuImage);
	const choose = (i: number) => {
		setSrc(slides[i].fullImage);
		setIndex(i);
	};

	return showCarousel ? (
		<ProductDetailsCarousel
			{...{
				slides,
				choose,
				index,
				src,
				alt: name,
				carouselRef,
				onTotalSlidesChange,
				onCurrentSlideChange,
			}}
		/>
	) : (
		<Grid container>
			<Grid item xs={12} sx={productDetailsImageSX}>
				<ProductImage {...{ src, alt: name, priority: true }} />
			</Grid>
		</Grid>
	);
};
